<template>
  <div class="l-block p-contentplans-writing">
    <div class="l-block__header">
      <TitleSub level="h2">
        <span class="title">タイトル・見出しを設計する</span>
        <div class="p-contentplans-writing__action">
          <router-link
            :to="{
              name: 'contentPlansSuggest',
              query: { createdAt: createdAt, keyword: keyword }
            }"
            target="_blank"
            class="c-link--blank"
          >
            サジェストマップを活用する
          </router-link>
        </div>
      </TitleSub>
    </div>
    <div class="l-block__content">
      <AiWritingBanner
        class="l-mt-4 l-mx-6"
        :projectId="project.id"
        :keyword="keyword"
      />
      <div v-if="!isEnableAiNewContent" class="p-contentplans-writing__status">
        <Advice img="pencil" priority="low-gray">
          <template slot="title">
            <p>
              タイトル・見出し設計機能をご利用の場合は、再調査を行ってください。
            </p>
          </template>
        </Advice>
      </div>
      <div v-else-if="isLoading" class="p-contentplans-writing__status">
        <LoadingChara variant="inline">
          <template slot="title">データ取得中</template>
        </LoadingChara>
      </div>
      <div v-else-if="isError" class="p-contentplans-writing__status">
        <Advice img="error" priority="low-gray">
          <template slot="title">
            <p>
              データが取得できませんでした。ページを再読み込みしてください。
            </p>
          </template>
          <ButtonReload size="small" />
        </Advice>
      </div>
      <div v-else class="p-contentplans-writing__content">
        <AiContainer ref="aiContainer">
          <div class="p-contentplans-writing__inner">
            <section>
              <div class="p-contentplans-writing__header">
                <h3 class="p-contentplans-writing__title">
                  ユーザーの検索意図・ゴールを設定する
                </h3>
              </div>
              <div class="p-contentplans-writing-ai__content">
                <AiKeywordIntent
                  :aiKeywordIntentData="aiAnswer.keywordIntent.data"
                  :error="aiAnswer.keywordIntent.error"
                  :isLoading="aiAnswer.keywordIntent.isLoading"
                />
                <AiGoal
                  v-if="phaseIndex[phase] >= phaseIndex.goal"
                  :error="aiAnswer.goal.error"
                  :userGoal="userInput.params.goal"
                  :aiKeywordIntentData="aiAnswer.keywordIntent.data"
                  :isCurrent="phase === 'goal'"
                  :isDisabled="isDisabled"
                  :isEditMode="isEditMode"
                  :isProcessing="aiAnswer.goal.isProcessing"
                  ref="aiGoal"
                  @submit="submitAiGoal"
                />
              </div>
            </section>
            <section
              v-if="phaseIndex[phase] >= phaseIndex.headings"
              ref="aiHeadings"
            >
              <div class="p-contentplans-writing__header">
                <h3 class="p-contentplans-writing__title">
                  タイトル・見出しを設計する
                </h3>
              </div>
              <div class="p-contentplans-writing-ai__content">
                <AiHeadings
                  :aiDescriptionData="aiAnswer.description.data"
                  :aiHeadingsData="parsedAiHeadingsData"
                  :error="aiAnswer.headings.error"
                  :userDescription="userInput.params.description"
                  :userHeadings="userInput.params.headings"
                  :isCurrent="phase === 'headings'"
                  :isDisabled="isDisabled"
                  :isEditMode="isEditMode"
                  :isLoading="
                    aiAnswer.headings.isLoading ||
                      aiAnswer.description.isLoading
                  "
                  :isProcessing="aiAnswer.headings.isProcessing"
                  @submit="submitAiHeadings"
                />
                <AiTitle
                  v-if="phaseIndex[phase] >= phaseIndex.title"
                  :aiTitleData="aiAnswer.title.data"
                  :error="aiAnswer.title.error"
                  :userSelectedTitle="userInput.params.selectedTitle"
                  :userTitle="userInput.params.title"
                  :isCurrent="phase === 'title'"
                  :isDisabled="isDisabled"
                  :isEditMode="isEditMode"
                  :isLoading="aiAnswer.title.isLoading"
                  :isProcessing="aiAnswer.title.isProcessing"
                  ref="aiTitle"
                  @submit="submitAiTitle"
                />
                <Serps
                  v-if="phaseIndex[phase] >= phaseIndex.serps"
                  :createdAt="createdAt"
                  :errorAddHeading="errorAddHeading"
                  :keyword="keyword"
                  :project="project"
                  :isEditMode="isEditMode"
                  ref="serps"
                  @addHeading="addUserHeading"
                />
              </div>
            </section>
          </div>
        </AiContainer>
        <UserContainer ref="userContainer">
          <div class="p-contentplans-writing__inner">
            <section>
              <div class="p-contentplans-writing__header">
                <h3 class="p-contentplans-writing__title">
                  コンテンツ構成案を作成する
                </h3>
                <template v-if="!isEditMode">
                  <ButtonRectangle priority="middle" @click="switchToEditMode"
                    >編集する</ButtonRectangle
                  >
                  <ButtonRadius
                    priority="low-white"
                    iconLeft="download"
                    @click="downloadFile"
                    >テキストダウンロード</ButtonRadius
                  ></template
                >
              </div>
              <div class="p-contentplans-writing-user__content">
                <UserGoal
                  v-model="userInput.params.goal"
                  :error="userInput.error.goal"
                  :maxLength="maxLength.goal"
                  :isEditMode="isEditMode"
                  ref="userGoal"
                />
                <UserHeadings
                  :errorAddHeading="errorAddHeading"
                  :errors="userInput.error.headings"
                  :h1="userInput.params.h1"
                  :headings="userInput.params.headings"
                  :headingsLength="headingsLength"
                  :isEditMode="isEditMode"
                  ref="userHeadings"
                  @addHeading="addUserHeading"
                  @inputHeading="inputUserHeading"
                  @insertHeading="insertUserHeading"
                  @removeHeading="removeUserHeading"
                >
                  <UserHeadingsH1
                    v-model="userInput.params.h1"
                    :error="userInput.error.h1"
                  />
                </UserHeadings>
                <UserDescription
                  v-model="userInput.params.description"
                  :error="userInput.error.description"
                  :maxLength="maxLength.description"
                  :isEditMode="isEditMode"
                  ref="userDescription"
                />
                <UserTitle
                  v-model="userInput.params.title"
                  :error="userInput.error.title"
                  :maxLength="maxLength.title"
                  :isEditMode="isEditMode"
                  ref="userTitle"
                />
                <UserAdvice />
              </div>
              <div
                v-if="isEditMode"
                class="p-contentplans-writing-user__footer"
              >
                <ButtonRectangle
                  priority="low-white"
                  :disabled="isDisabledSaveButton"
                  @click="submitQuit"
                  >編集を中断する</ButtonRectangle
                >
                <div class="c-alertPos">
                  <ButtonRectangle
                    priority="low-white"
                    :disabled="isDisabledSaveButton"
                    :class="{ 'is-loading': save.isProcessing.save }"
                    @click="submitSave('save')"
                    >一時保存</ButtonRectangle
                  >
                  <Alert v-if="save.error.save" variant="center">{{
                    save.error.save
                  }}</Alert>
                </div>
                <div class="c-alertPos">
                  <ButtonRectangle
                    priority="middle"
                    iconLeft="download"
                    :disabled="isDisabledSaveButton"
                    :class="{ 'is-loading': save.isProcessing.download }"
                    @click="submitSave('download')"
                  >
                    保存してダウンロード
                  </ButtonRectangle>
                  <Alert v-if="save.error.download" variant="center">{{
                    save.error.download
                  }}</Alert>
                </div>
              </div>
            </section>
          </div>
        </UserContainer>
      </div>
    </div>
    <ModalEditedAlert ref="modalEditedAlert" />
  </div>
</template>

<script>
import Advice from '@/components/Advice';
import AiContainer from '@/components/contentPlans/writing/AiContainer';
import AiGoal from '@/components/contentPlans/writing/AiGoal';
import AiHeadings from '@/components/contentPlans/writing/AiHeadings';
import AiKeywordIntent from '@/components/contentPlans/writing/AiKeywordIntent';
import AiTitle from '@/components/contentPlans/writing/AiTitle';
import AiWritingBanner from '@/components/aiWriting/AiWritingBanner';
import Alert from '@/components/Alert';
import ButtonRadius from '@/components/ButtonRadius';
import ButtonRectangle from '@/components/ButtonRectangle';
import ButtonReload from '@/components/ButtonReload';
import LoadingChara from '@/components/LoadingChara';
import ModalEditedAlert from '@/components/contentPlans/writing/ModalEditedAlert.vue';
import Serps from '@/components/contentPlans/writing/Serps';
import TitleSub from '@/components/TitleSub';
import UserAdvice from '@/components/contentPlans/writing/UserAdvice';
import UserContainer from '@/components/contentPlans/writing/UserContainer';
import UserDescription from '@/components/contentPlans/writing/UserDescription';
import UserGoal from '@/components/contentPlans/writing/UserGoal';
import UserHeadings from '@/components/contentPlans/writing/UserHeadings';
import UserHeadingsH1 from '@/components/contentPlans/writing/UserHeadingsH1';
import UserTitle from '@/components/contentPlans/writing/UserTitle';
import utils from '@/utils';
import Validation from '@/validation';
import { v4 as uuidv4 } from 'uuid';
import Amplitude from '@/amplitude';
import axios from 'axios';
import apiProjects from '@/api/projects';

const phaseIndex = {
  keywordIntent: 1,
  goal: 2,
  headings: 3,
  description: 4,
  title: 5,
  serps: 6
};

const plotType = {
  keywordIntent: 'nc-keyword-intent',
  goal: 'nc-goal',
  headings: 'nc-headers',
  description: 'nc-description',
  title: 'nc-title'
};

const maxLength = {
  keywordIntent: 100,
  goal: 100,
  title: 100,
  description: 300,
  heading: 100,
  headings: 100
};

export default {
  props: ['createdAt', 'keyword', 'project'],
  components: {
    Advice,
    AiContainer,
    AiGoal,
    AiHeadings,
    AiKeywordIntent,
    AiTitle,
    AiWritingBanner,
    Alert,
    ButtonRadius,
    ButtonRectangle,
    ButtonReload,
    LoadingChara,
    ModalEditedAlert,
    Serps,
    TitleSub,
    UserAdvice,
    UserContainer,
    UserDescription,
    UserGoal,
    UserHeadings,
    UserHeadingsH1,
    UserTitle
  },
  data() {
    return {
      aiAnswer: {
        keywordIntent: {
          data: null,
          error: '',
          isLoading: false,
          isProcessing: false
        },
        goal: {
          error: '',
          isProcessing: false
        },
        headings: {
          data: null,
          error: '',
          isLoading: false,
          isProcessing: false
        },
        description: {
          data: null,
          error: '',
          isLoading: false,
          isProcessing: false
        },
        title: {
          data: null,
          error: '',
          isLoading: false,
          isProcessing: false
        }
      },
      contentPlanId: '',
      phase: '',
      request: {
        cancelToken: {},
        expiresAt: Date.now() + 10 * 60 * 1000,
        timeoutDelay: 10000,
        timeoutId: {}
      },
      save: {
        error: {
          download: '',
          save: ''
        },
        isProcessing: {
          download: false,
          save: false
        }
      },
      userInput: {
        error: {
          goal: '',
          title: '',
          description: '',
          h1: '',
          headings: {}
        },
        params: {
          selectedGoal: '',
          goal: '',
          selectedTitle: '',
          title: '',
          selectedDescription: '',
          description: '',
          h1: '',
          headings: []
        },
        oldParams: null,
        isEdited: false,
        isError: false
      },
      isEditMode: false,
      isError: false,
      isLoading: true
    };
  },
  computed: {
    errorAddHeading() {
      if (this.headingsLength.h1234 >= this.maxLength.headings) {
        return `見出しはh4も含めて${this.maxLength.headings}個までしか追加できません`;
      }
      return '';
    },
    headingsLength() {
      const headings = this.userInput.params.headings;
      let h1, h2, h3;
      // 編集モードでは枠の数を、閲覧モードでは空ではない値の数をカウントする
      if (this.isEditMode) {
        h1 = 1;
        h2 = headings.filter(item => item.level === 'h2').length;
        h3 = headings.filter(item => item.level === 'h3').length;
      } else {
        h1 = this.userInput.params.h1 ? 1 : 0;
        h2 = headings.filter(item => item.level === 'h2' && item.value).length;
        h3 = headings.filter(item => item.level === 'h3' && item.value).length;
      }
      const h123 = h1 + h2 + h3;
      const h1234 = h1 + headings.length;
      return { h1, h2, h3, h123, h1234 };
    },
    maxLength() {
      return maxLength;
    },
    phaseIndex() {
      return phaseIndex;
    },
    plotType() {
      return plotType;
    },
    isDisabled() {
      return this.userInput.isError ? true : false;
    },
    isDisabledSaveButton() {
      return this.isDisabled ||
        this.save.isProcessing.download ||
        this.save.isProcessing.save
        ? true
        : false;
    },
    isEnableAiNewContent() {
      // dateJst日付形式で分析日が送信されているので、同じ形式に変換
      const date = this.$options.filters.dateText(
        process.env.VUE_APP_CONTENT_PLAN_ADD_AI_NEW_CONTENT_DATE,
        'YYYY-MM-DD'
      );
      return utils.isSameOrAfterDate(this.createdAt, date);
    },
    parsedAiHeadingsData() {
      let items = [];
      if (this.aiAnswer.headings.data) {
        const { h234 } = this.getParsedHeadings(
          this.aiAnswer.headings.data.items
        );
        items = h234;
      }
      return { items };
    }
  },
  watch: {
    'userInput.error': {
      handler(error) {
        this.userInput.isError =
          error.goal ||
          error.title ||
          error.description ||
          error.h1 ||
          Object.keys(error.headings).length
            ? true
            : false;
      },
      deep: true
    },
    'userInput.params': {
      handler() {
        this.userInput.isEdited = true;
      },
      deep: true
    },
    'userInput.params.goal': {
      handler(value) {
        this.userInput.error.goal = new Validation()
          .maxLength(maxLength.goal)
          .test(value);
      },
      immediate: true
    },
    'userInput.params.title': {
      handler(value) {
        this.userInput.error.title = new Validation()
          .maxLength(maxLength.title)
          .test(value);
      },
      immediate: true
    },
    'userInput.params.description': {
      handler(value) {
        this.userInput.error.description = new Validation()
          .maxLength(maxLength.description)
          .test(value);
      },
      immediate: true
    },
    'userInput.params.h1': {
      handler(value) {
        this.userInput.error.h1 = new Validation()
          .maxLength(maxLength.heading)
          .test(value);
      },
      immediate: true
    },
    'userInput.params.headings': {
      handler(headings) {
        const errors = {};
        // 100個以上追加できないはずだが101個以上の場合はエラーにする（+1はh1の分）
        if (headings.length + 1 > this.maxLength.headings) {
          errors.maxLength = `${this.maxLength.headings}個以内にしてください`;
        }
        headings.forEach(heading => {
          const error = new Validation()
            .maxLength(maxLength.heading)
            .test(heading.value);

          if (error) {
            errors[heading.id] = error;
          }
        });
        this.userInput.error.headings = errors;
      },
      immediate: true
    }
  },
  created() {
    if (!this.createdAt || !this.keyword) {
      this.goBack();
    }
    this.getKeywords();
  },
  beforeDestroy() {
    this.cancelRequests();
  },
  beforeRouteLeave(to, from, next) {
    if (this.userInput.isEdited) {
      this.$refs.modalEditedAlert.openModal(next);
    } else {
      next();
    }
  },
  methods: {
    addUserHeading(level, value) {
      this.userInput.params.headings.push({ id: uuidv4(), level, value });
      this.$refs.userHeadings.focusLastInput();
      this.$refs.userHeadings.scrollToEnd();
    },
    cancelRequests() {
      for (const key in this.request.timeoutId) {
        const timeoutId = this.request.timeoutId[key];
        clearTimeout(timeoutId);
      }
      for (const key in this.request.cancelToken) {
        const cancelToken = this.request.cancelToken[key];
        cancelToken.cancel();
      }
    },
    downloadFile() {
      const keyword = `キーワード「${this.keyword}」`;
      const goal = `どのようなユーザーに、読後にどう思ってもらいたいか\n${this.userInput.params.goal}`;
      const title = `タイトル案\n${this.userInput.params.title}`;
      const description = `ディスクリプション案\n${this.userInput.params.description}`;

      const h1 = this.userInput.params.h1
        ? `h1: ${this.userInput.params.h1}\n`
        : ``;
      const h234 = this.userInput.params.headings
        .filter(heading => heading.value)
        .map(item => {
          const depth = parseInt(item.level.slice(1)) - 1;
          return `${'-'.repeat(depth)}${item.level}: ${item.value}`;
        })
        .join('\n');
      const headings = `見出し構成案\n${h1}${h234}`;

      const data = [keyword, goal, title, description, headings].join('\n\n');
      const date = utils.formatDate(new Date(), 'YYYY年MM月DD日');
      const fileName = `コンテンツ立案_${this.keyword}_${date}_${this.project.name}.txt`;
      utils.downloadFile(data, fileName);
      Amplitude.postLogEvent('DownloadContentPlanPlotText');
    },
    focusPhaseGoal() {
      if (!this.isEditMode || this.userInput.params.selectedGoal) {
        return;
      }
      this.$nextTick(() => {
        this.$refs.aiContainer.scroll(this.$refs.aiGoal.$el);
        // ユーザーゴールの入力欄にフォーカスを当てる
        this.$refs.userGoal.focusInput();
      });
    },
    focusPhaseHeadings() {
      if (!this.isEditMode || this.userInput.params.selectedDescription) {
        return;
      }
      if (
        this.aiAnswer.headings.isLoading ||
        this.aiAnswer.description.isLoading
      ) {
        return;
      }
      // パースできない形式の場合は、見出し構成案の入力欄を追加する
      if (
        !this.aiAnswer.headings.data ||
        !this.aiAnswer.headings.data.items ||
        !this.aiAnswer.headings.data.items.length
      ) {
        this.addUserHeading('h2', '');
      } else if (!this.aiAnswer.description.data) {
        // ディスクリプションの入力欄にフォーカスを当てる
        this.$refs.userDescription.focusInput();
      }
      this.$nextTick(() => {
        this.$refs.aiContainer.scroll(this.$refs.aiHeadings);
      });
    },
    focusPhaseTitle() {
      if (!this.isEditMode || this.userInput.params.selectedTitle) {
        return;
      }
      // パースできない形式の場合は、タイトルの入力欄にフォーカスを当てる
      if (
        !this.aiAnswer.title.data ||
        !this.aiAnswer.title.data.items ||
        !this.aiAnswer.title.data.items.length
      ) {
        this.$refs.userTitle.focusInput();
      }
      this.$nextTick(() => {
        this.$refs.aiContainer.scroll(this.$refs.aiTitle.$el);
      });
    },
    focusSerps() {
      if (!this.isEditMode) {
        return;
      }
      this.$nextTick(() => {
        this.$refs.aiContainer.scroll(this.$refs.serps.$el);
      });
    },
    async getKeywords() {
      this.request.cancelToken.keywords = axios.CancelToken.source();

      // contentPlanIdを獲得するために、キーワード一覧を取得してクエリパラメータのkeywordとcreatedAtが一致するものを探す
      // キーワード登録のレスポンスが空でidが返ってこないので、こんなことをしている
      const [response, error] = await apiProjects.contentPlans.keywords
        .get(
          this.auth.idToken,
          this.project.id,
          this.request.cancelToken.keywords.token
        )
        .then(response => [response, null])
        .catch(error => {
          return axios.isCancel(error) ? [null, null] : [null, error];
        });

      if (response) {
        const contentPlanKeyword = response.data.items.find(item => {
          const isSameDay = utils.isSameDay(this.createdAt, item.createdAt);
          return item.keyword === this.keyword && isSameDay;
        });
        // keywordとcreatedAtが一致するデータがない場合は、不正なキーワードor日付でアクセスされたと判断して一覧画面に戻す
        if (!contentPlanKeyword) {
          this.goBack();
          return;
        }
        this.contentPlanId = contentPlanKeyword.id;

        // AI機能が使える場合はデータ取得に進む
        if (this.isEnableAiNewContent) {
          this.getPlots();
        }
        Amplitude.postLogEvent('ViewContentPlanPlotPage', {
          available: this.isEnableAiNewContent
        });
      } else if (error) {
        this.goBack();
      }
    },
    async getPlots() {
      this.request.cancelToken.plots = axios.CancelToken.source();

      const [response, error] = await apiProjects.contentPlans.plots
        .get(
          this.auth.idToken,
          this.project.id,
          this.contentPlanId,
          this.request.cancelToken.plots.token
        )
        .then(response => [response, null])
        .catch(error => {
          return axios.isCancel(error) ? [null, null] : [null, error];
        });

      if (response) {
        const {
          'nc-goal': goal,
          'nc-title': title,
          'nc-description': description,
          'nc-headers': headings
        } = response.data;

        const { h1, h234 } = this.getParsedHeadings(
          headings ? headings.inputList : []
        );

        this.userInput.params = {
          selectedGoal: goal && goal.select ? goal.select : '',
          goal: goal && goal.input ? goal.input : '',
          selectedTitle: title && title.select ? title.select : '',
          title: title && title.input ? title.input : '',
          selectedDescription:
            description && description.select ? description.select : '',
          description:
            description && description.input ? description.input : '',
          h1: h1,
          headings: h234
        };
        this.userInput.oldParams = JSON.parse(
          JSON.stringify(this.userInput.params)
        );
        this.$nextTick(() => {
          this.userInput.isEdited = false;
        });

        // タイトルの選択まで進んでいない場合は編集モードにする
        if (!this.userInput.params.selectedTitle) {
          this.switchToEditMode();
        }
        this.isLoading = false;
        this.phase = 'keywordIntent';
        this.initPhaseKeywordIntent();
      } else if (error) {
        this.isError = true;
        this.isLoading = false;
      }
    },
    getParsedHeadings(headings) {
      let h1 = '';
      const h234 = [];
      if (headings && headings.length) {
        headings.forEach(item => {
          const result = item.match(/^(h\d):(.*)$/);
          if (result && result[2]) {
            if (result[1] === 'h1') {
              h1 = result[2];
            } else if (result[2]) {
              h234.push({ id: uuidv4(), level: result[1], value: result[2] });
            }
          }
        });
      }
      return { h1, h234 };
    },
    goBack() {
      this.$router.push({ name: 'contentPlans' });
    },
    async initPhase() {
      // フェーズ判定をする
      if (this.phase === 'keywordIntent') {
        this.phase = 'goal';
        this.initPhaseGoal();
      } else if (this.phase === 'goal' && this.userInput.params.selectedGoal) {
        this.phase = 'headings';
        this.initPhaseHeadings();
        this.initPhaseDescription();
      } else if (
        this.phase === 'headings' &&
        this.userInput.params.selectedDescription
      ) {
        this.phase = 'title';
        this.initPhaseTitle();
      } else if (
        this.phase === 'title' &&
        this.userInput.params.selectedTitle
      ) {
        this.phase = 'serps';
        this.initPhaseSerps();
      }
    },
    async initPhaseKeywordIntent() {
      await this.postAnswer('keywordIntent');
    },
    initPhaseGoal() {
      this.focusPhaseGoal();
      this.initPhase();
    },
    async initPhaseHeadings() {
      if (this.isEditMode) {
        this.$nextTick(() => {
          this.$refs.aiContainer.scroll(this.$refs.aiHeadings);
        });
      }
      await this.postAnswer('headings', this.focusPhaseHeadings);
    },
    async initPhaseDescription() {
      await this.postAnswer('description', this.focusPhaseHeadings);
    },
    async initPhaseTitle() {
      if (this.isEditMode) {
        this.$nextTick(() => {
          this.$refs.aiContainer.scroll(this.$refs.aiTitle.$el);
        });
      }
      await this.postAnswer('title', this.focusPhaseTitle);
    },
    initPhaseSerps() {
      this.focusSerps();
    },
    inputUserHeading(id, level, value) {
      const index = this.userInput.params.headings.findIndex(
        item => item.id === id
      );
      this.userInput.params.headings.splice(index, 1, { id, level, value });
    },
    insertUserHeading(id, level, value) {
      const index = this.userInput.params.headings.findIndex(
        item => item.id === id
      );
      if (index !== -1) {
        this.userInput.params.headings.splice(index + 1, 0, {
          id: uuidv4(),
          level,
          value
        });
        // <UserHeadingsListItem />のkeyをitem.idにしているので、items[index+1]のinputにフォーカスしようとしてもうまくいかない
        // 配列の途中にinsertしてもVueの内部的には最後に追加される
        this.$refs.userHeadings.focusLastInput();
      }
    },
    async postAnswer(phase, callbackFn) {
      this.aiAnswer[phase].isLoading = true;
      this.request.cancelToken[phase] = axios.CancelToken.source();

      const params = {};
      const [response, error] = await apiProjects.contentPlans.answer
        .post(
          params,
          this.auth.idToken,
          this.project.id,
          this.contentPlanId,
          plotType[phase],
          this.request.cancelToken[phase].token
        )
        .then(response => [response, null])
        .catch(error => {
          return axios.isCancel(error) ? [null, null] : [null, error];
        });

      if (response && response.data) {
        this.aiAnswer[phase].data = response.data;
        this.aiAnswer[phase].isLoading = false;
        this.initPhase();
        if (callbackFn) {
          callbackFn();
        }
      } else if (response) {
        this.request.timeoutId[phase] = setTimeout(() => {
          this.postAnswer(phase, callbackFn);
        }, this.request.timeoutDelay);
      } else if (error) {
        this.aiAnswer[phase].isLoading = false;
        this.initPhase();
        if (callbackFn) {
          callbackFn();
        }
      }
    },
    async putPlot(phase) {
      if (this.aiAnswer[phase].isProcessing) {
        return;
      }
      this.aiAnswer[phase].isProcessing = true;
      this.aiAnswer[phase].error = '';
      this.request.cancelToken[phase] = axios.CancelToken.source();

      const [response, error] = await this.putPlots(
        this.request.cancelToken[phase].token
      );

      if (response) {
        this.aiAnswer[phase].isProcessing = false;
      } else if (error) {
        this.aiAnswer[phase].error =
          'エラーが発生しました。もう一度クリックしてください';
        this.aiAnswer[phase].isProcessing = false;

        this.request.timeoutId[phase] = setTimeout(() => {
          this.aiAnswer[phase].error = '';
        }, 2000);
      }
      return [response, error];
    },
    async putPlots(cancelToken) {
      // h1はheadingsの先頭に入れて送る必要があるため
      const headings = [];
      if (this.userInput.params.h1) {
        headings.push(`h1:${this.userInput.params.h1}`);
      }
      this.userInput.params.headings.forEach(h => {
        if (h.value) {
          headings.push(`${h.level}:${h.value}`);
        }
      });

      const params = {
        'nc-goal': {
          input: this.userInput.params.goal,
          select: this.userInput.params.selectedGoal
        },
        'nc-headers': {
          inputList: headings
        },
        'nc-description': {
          input: this.userInput.params.description,
          select: this.userInput.params.selectedDescription
        },
        'nc-title': {
          input: this.userInput.params.title,
          select: this.userInput.params.selectedTitle
        }
      };

      const [response, error] = await apiProjects.contentPlans.plots
        .put(
          params,
          this.auth.idToken,
          this.project.id,
          this.contentPlanId,
          cancelToken
        )
        .then(response => {
          this.userInput.oldParams = JSON.parse(
            JSON.stringify(this.userInput.params)
          );
          this.userInput.isEdited = false;
          return [response, null];
        })
        .catch(error => {
          return axios.isCancel(error) ? [null, null] : [null, error];
        });

      return [response, error];
    },
    removeUserHeading(id) {
      const index = this.userInput.params.headings.findIndex(
        item => item.id === id
      );
      this.userInput.params.headings.splice(index, 1);
    },
    async submitAiGoal() {
      this.userInput.params.selectedGoal = this.userInput.params.goal;

      const [response] = await this.putPlot('goal');
      if (response) {
        this.initPhase();
      }
    },
    async submitAiHeadings(isParsedDescription, isParsedHeadings) {
      if (isParsedDescription) {
        this.userInput.params.selectedDescription = this.aiAnswer.description.data.items[0];
        this.userInput.params.description = this.aiAnswer.description.data.items[0];
      } else {
        this.userInput.params.selectedDescription = this.userInput.params.description;
      }
      if (isParsedHeadings) {
        this.userInput.params.headings = this.parsedAiHeadingsData.items.map(
          h => {
            return { ...h };
          }
        );
      }
      if (!this.userInput.params.goal) {
        this.userInput.error.goal =
          '「どのようなユーザーに、読後にどう思ってもらいたいか」が記入されていないため次へ進めません';
        this.$nextTick(() => {
          this.$refs.userContainer.scroll(this.$refs.userGoal.$el);
        });
        this.$refs.userGoal.focusInput();
        return;
      }
      // 転記したのがわかるように見出し構成案の入力欄までスクロールする
      this.$nextTick(() => {
        this.$refs.userContainer.scroll(this.$refs.userHeadings.$el);
      });

      const [response] = await this.putPlot('headings');
      if (response) {
        this.initPhase();
      }
    },
    async submitAiTitle(selected) {
      if (selected) {
        this.userInput.params.h1 = selected;
        this.userInput.params.selectedTitle = selected;
        this.userInput.params.title = selected;
      } else {
        // タイトルをh1に転記する
        this.userInput.params.h1 = this.userInput.params.title;
        this.userInput.params.selectedTitle = this.userInput.params.title;
      }
      // 転記したのがわかるようにタイトルの入力欄までスクロールする
      this.$nextTick(() => {
        this.$refs.userContainer.scroll(this.$refs.userTitle.$el);
      });

      const [response] = await this.putPlot('title');
      if (response) {
        this.initPhase();
      }
    },
    async submitSave(context) {
      if (this.isDisabledSaveButton) {
        return;
      }
      this.save.isProcessing[context] = true;
      // 初期化
      this.save.error.download = '';
      this.save.error.save = '';

      this.request.cancelToken.save = axios.CancelToken.source();

      const [response, error] = await this.putPlots(
        this.request.cancelToken.save.token
      );

      if (response && context === 'download') {
        this.downloadFile();
        this.switchToViewMode();
      } else if (error) {
        this.save.error[context] =
          '保存に失敗しました。もう一度クリックしてください';
        clearTimeout(this.request.timeoutId.save);
        this.request.timeoutId.save = setTimeout(() => {
          this.save.error[context] = '';
        }, 2000);
      }

      this.save.isProcessing[context] = false;
    },
    async submitQuit() {
      if (this.userInput.isEdited) {
        const callbackQuit = () => {
          this.userInput.params = JSON.parse(
            JSON.stringify(this.userInput.oldParams)
          );
          this.$nextTick(() => {
            this.userInput.isEdited = false;
          });
          this.switchToViewMode();
        };
        this.$refs.modalEditedAlert.openModal(callbackQuit);
      } else {
        this.switchToViewMode();
      }
    },
    switchToEditMode() {
      this.isEditMode = true;
    },
    switchToViewMode() {
      // 空の見出しを削除する
      this.userInput.params.headings = this.userInput.params.headings.filter(
        heading => heading.value
      );
      this.$nextTick(() => {
        this.userInput.isEdited = false;
      });

      this.isEditMode = false;
      this.$nextTick(() => {
        this.$refs.userContainer.scrollToTop();
      });
    }
  }
};
</script>
